'use client';

import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Category } from '@/__generated_graphql__/graphql';
import { CategoryPicker } from '@/components/CategoryPicker/CategoryPicker';
import { SearchBar } from '@/components/SearchBar/SearchBar';
import { SubFooter } from '@/components/SubFooter/SubFooter';
import { SubFooterMobile } from '@/components/SubFooterMobile/SubFooterMobile';
import { DashboardBackgroundIcon } from '@/components/ui/Icons/Icons';
import { ParticipantEnum } from '@/constants/common';
import { useDashboard } from '@/hooks/useDashboard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useVisibleElement } from '@/hooks/useVisibleElement';
import { useParticipant } from '@/providers/ParticipantProvider';
import { SpaceColumn } from '@/styles/common';

import { useHomeContext } from '../Home/Home.provider';
import { AdsBlock } from './AdsBlock/AdsBlock';
import { AdStatisticBar } from './AdStatisticBar';
import * as S from './Dashboard.styles';

type DashboardProps = {
  categories: Category[];
};

export const Dashboard: FC<DashboardProps> = ({ categories }) => {
  const { toggleVisibleHeader } = useHomeContext();
  const { t } = useTranslation();

  const isTablet = useMediaQuery('tablet');
  const ref = useRef<HTMLDivElement | null>(null);

  useVisibleElement({ element: ref.current, offset: -80 }, toggleVisibleHeader);

  const { participant, isResumes, loading } = useParticipant();

  const { statistic } = useDashboard(participant);

  const isBig = !isTablet;

  const subFooter = isBig ? (
    <SubFooter categories={categories} />
  ) : (
    <SubFooterMobile categories={categories} />
  );

  const participantLower = participant.toLowerCase();
  const searchBlockTitle = t(`MainPage.search_block_${participantLower}_title`);

  return (
    <S.Wrapper>
      <S.MainContentWrapper $isResumes={isResumes} ref={ref}>
        <S.SectionContent>
          <S.SearchBlockWrapper>
            <S.SearchBlockTitle $isResumes={isResumes}>
              {searchBlockTitle}
            </S.SearchBlockTitle>
            <SpaceColumn $gap={30}>
              <AdStatisticBar
                statistic={statistic}
                isResumes={participant === ParticipantEnum.RESUME}
              />
              <SearchBar isBig={isBig} />
            </SpaceColumn>
            <CategoryPicker isBig={isBig} categories={categories} />
          </S.SearchBlockWrapper>
        </S.SectionContent>

        <S.BackgroundIconWrapper $participant={participant}>
          <DashboardBackgroundIcon />
        </S.BackgroundIconWrapper>
      </S.MainContentWrapper>

      {!loading && <AdsBlock participant={participant} />}
      {subFooter}
    </S.Wrapper>
  );
};
